/**
 * KanbanView — cards grouped into lanes by the board's first status column.
 * Drag a card between lanes to change its status. Lane headers are editable
 * (rename / recolor / delete) and a new lane appends a status option.
 */

import React, { useEffect, useRef, useState } from 'react';
import { PlusIcon, EllipsisHorizontalIcon, TrashIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import type { BoardColumn, BoardFull, BoardRow, StatusOption } from './types';
import { STATUS_COLORS } from './cells';
import { resolveRoles, rowTitle, personInitials, personColor, statusOption } from './boardHelpers';
import { ThumbImg } from './FileDrawer';
import { useUsersForBoards } from './api';

const COLOR_KEYS = Object.keys(STATUS_COLORS);

interface Lane extends StatusOption { fixed?: boolean }

const KanbanCard: React.FC<{
  boardId: string;
  board: BoardFull;
  row: BoardRow;
  laneColor: string;
  onOpen: (id: string) => void;
  onDragStart: (id: string) => void;
  dragging: boolean;
}> = ({ boardId, board, row, laneColor, onOpen, onDragStart, dragging }) => {
  const roles = resolveRoles(board.columns);
  const users = useUsersForBoards().data || [];
  const palette = STATUS_COLORS[laneColor] || STATUS_COLORS.gray;
  const photos = (row.attachments || []).filter(a => a.kind === 'photo').slice(0, 3);
  const personVal = roles.personCol ? row.values[roles.personCol.id] : null;
  const person = users.find(u => u.id === personVal);
  const progress = roles.progressCol ? Number(row.values[roles.progressCol.id] ?? 0) : null;

  const chips = roles.chipCols
    .filter(c => c.id !== roles.statusCol?.id)
    .map(c => {
      const opt = statusOption(c, row.values[c.id]) ||
        ((c.config?.options || []) as { id: string; label: string }[]).find(o => o.id === row.values[c.id]);
      if (!opt) return null;
      const color = (opt as StatusOption).color || 'gray';
      return { key: c.id, label: (opt as { label: string }).label, color };
    })
    .filter(Boolean) as { key: string; label: string; color: string }[];

  return (
    <div
      className={`bd-kcard ${dragging ? 'bd-kcard--drag' : ''}`}
      draggable
      onDragStart={() => onDragStart(row.id)}
      onClick={() => onOpen(row.id)}
    >
      <div className="bd-kcard-top" style={{ background: palette.fg }} />
      <div className="bd-kcard-name">{rowTitle(row, roles.titleCol)}</div>
      {chips.length > 0 && (
        <div className="bd-kcard-chips">
          {chips.map(c => {
            const p = STATUS_COLORS[c.color] || STATUS_COLORS.gray;
            return <span key={c.key} className="bd-chip" style={{ background: p.bg, color: p.fg }}>{c.label}</span>;
          })}
        </div>
      )}
      {photos.length > 0 && (
        <div className="bd-kcard-thumbs">
          {photos.map(ph => <div className="bd-kcard-thumb" key={ph.id}><ThumbImg boardId={boardId} att={ph} /></div>)}
        </div>
      )}
      <div className="bd-kcard-foot">
        {person && (
          <span className="bd-avatar bd-avatar--sm" style={{ background: personColor(person.id) }}>
            {personInitials(`${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email)}
          </span>
        )}
        {progress != null && (
          <div className="bd-prog">
            <div className="bd-prog-track"><div className="bd-prog-fill" style={{ width: `${Math.min(100, progress)}%` }} /></div>
            <span className="bd-prog-val">{progress}%</span>
          </div>
        )}
        {(row.attachments || []).length > 0 && (
          <span className="bd-kcard-docs"><PaperClipIcon className="w-3.5 h-3.5" />{row.attachments!.length}</span>
        )}
      </div>
    </div>
  );
};

const LaneHeader: React.FC<{
  lane: Lane;
  count: number;
  onRename: (id: string, label: string) => void;
  onColor: (id: string, color: string) => void;
  onDelete: (id: string) => void;
}> = ({ lane, count, onRename, onColor, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const palette = STATUS_COLORS[lane.color] || STATUS_COLORS.gray;
  return (
    <div className="bd-kcol-hd" ref={ref}>
      <span className="bd-kcol-bar" style={{ background: palette.fg }} />
      {lane.fixed ? (
        <span className="bd-kcol-name">{lane.label}</span>
      ) : (
        <span
          className="bd-kcol-name bd-kcol-name--edit"
          contentEditable
          suppressContentEditableWarning
          onBlur={e => onRename(lane.id, e.currentTarget.textContent?.trim() || lane.label)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); } }}
        >
          {lane.label}
        </span>
      )}
      <span className="bd-kcol-count">{count}</span>
      {!lane.fixed && (
        <button className="bd-kcol-menu" onClick={() => setMenu(m => !m)} title="Modifier la colonne">
          <EllipsisHorizontalIcon className="w-4 h-4" />
        </button>
      )}
      {menu && !lane.fixed && (
        <div className="bd-pop bd-kcol-pop">
          <div className="bd-pop-title">Couleur de la colonne</div>
          <div className="bd-color-grid">
            {COLOR_KEYS.map(c => (
              <button key={c} className={`bd-color-sw ${c === lane.color ? 'bd-color-sw--on' : ''}`}
                style={{ background: STATUS_COLORS[c].fg }}
                onClick={() => { onColor(lane.id, c); setMenu(false); }} />
            ))}
          </div>
          <button className="bd-pop-clear" onClick={() => { onDelete(lane.id); setMenu(false); }}>
            <TrashIcon className="w-4 h-4" /> Supprimer la colonne
          </button>
        </div>
      )}
    </div>
  );
};

export const KanbanView: React.FC<{
  boardId: string;
  board: BoardFull;
  statusCol: BoardColumn | null;
  rows: BoardRow[];
  onMoveStatus: (rowId: string, statusId: string | null) => void;
  onOpenItem: (rowId: string) => void;
  onUpdateStatusOptions: (options: StatusOption[]) => void;
  onCreateStatusColumn: () => void;
}> = ({ boardId, board, statusCol, rows, onMoveStatus, onOpenItem, onUpdateStatusOptions, onCreateStatusColumn }) => {
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  if (!statusCol) {
    return (
      <div className="bd-view-empty">
        La vue Kanban a besoin d'une colonne de type <strong>Statut</strong> pour organiser vos cartes en colonnes.
        <div style={{ marginTop: 16 }}>
          <button className="bd-btn-primary" onClick={onCreateStatusColumn}>
            <PlusIcon className="w-4 h-4" /> Créer une colonne « Statut »
          </button>
        </div>
      </div>
    );
  }

  const options = (statusCol.config?.options || []) as StatusOption[];
  const lanes: Lane[] = [...options, { id: '__none__', label: 'Sans statut', color: 'gray', fixed: true }];
  const laneOf = (r: BoardRow) => {
    const v = r.values[statusCol.id];
    return options.some(o => o.id === v) ? (v as string) : '__none__';
  };

  const rename = (id: string, label: string) => onUpdateStatusOptions(options.map(o => o.id === id ? { ...o, label } : o));
  const recolor = (id: string, color: string) => onUpdateStatusOptions(options.map(o => o.id === id ? { ...o, color } : o));
  const remove = (id: string) => {
    if (!confirm('Supprimer cette colonne ? Les cartes passeront en « Sans statut ».')) return;
    onUpdateStatusOptions(options.filter(o => o.id !== id));
  };
  const addLane = () => {
    const used = new Set(options.map(o => o.color));
    const color = COLOR_KEYS.find(c => !used.has(c)) || COLOR_KEYS[options.length % COLOR_KEYS.length];
    onUpdateStatusOptions([...options, { id: `opt-${Date.now()}`, label: 'Nouvelle colonne', color }]);
  };

  return (
    <div className="bd-kanban">
      {lanes.map(lane => {
        const cards = rows.filter(r => laneOf(r) === lane.id);
        const targetId = lane.fixed ? null : lane.id;
        return (
          <div className="bd-kcol" key={lane.id}>
            <LaneHeader lane={lane} count={cards.length} onRename={rename} onColor={recolor} onDelete={remove} />
            <div
              className={`bd-kcol-body ${over === lane.id ? 'bd-kcol-body--over' : ''}`}
              onDragOver={e => { e.preventDefault(); setOver(lane.id); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null); }}
              onDrop={() => { if (drag) onMoveStatus(drag, targetId); setDrag(null); setOver(null); }}
            >
              {cards.map(r => (
                <KanbanCard key={r.id} boardId={boardId} board={board} row={r} laneColor={lane.color}
                  onOpen={onOpenItem} onDragStart={setDrag} dragging={drag === r.id} />
              ))}
              {cards.length === 0 && <div className="bd-kcol-empty">Glissez une carte ici</div>}
            </div>
          </div>
        );
      })}
      <div className="bd-kcol bd-kcol-add">
        <button className="bd-kadd-btn" onClick={addLane}><PlusIcon className="w-4 h-4" /> Ajouter une colonne</button>
      </div>
    </div>
  );
};
